import classnames from 'classnames';
import { memo, useCallback, useEffect, useState, type ChangeEvent, type FocusEvent, type FunctionComponent, type KeyboardEvent } from 'react';
import { translateFormatter } from '@/domains/core/localization/translate';
import { InputText } from '@/domains/core/tamagoshiTailwind/components/Input/InputText';
import capitalizeFirstLetter from '@/domains/productDiscovery/Listings/utils/string/capitalizeFirstLetter/capitalizeFirstLetter';
import styles from './RangeSelect.module.scss';
interface Props {
  className?: string;
  id: string;
  min: number;
  max: number;
  values: {
    min: number;
    max: number;
  };
  unit?: string;
  onChange?(event: FocusEvent | KeyboardEvent, {
    min,
    max
  }: {
    min: number;
    max: number;
  }): void;
}
const translationsMin = translateFormatter<{
  unit?: string;
}>([{
  "type": 5,
  "value": "unit",
  "options": {
    "undefined": {
      "value": [{
        "type": 0,
        "value": "Minimum"
      }]
    },
    "other": {
      "value": [{
        "type": 0,
        "value": "("
      }, {
        "type": 1,
        "value": "unit"
      }, {
        "type": 0,
        "value": ") Minimum"
      }]
    }
  }
}], undefined, undefined, "en");
const translationsMax = translateFormatter<{
  unit?: string;
}>([{
  "type": 5,
  "value": "unit",
  "options": {
    "undefined": {
      "value": [{
        "type": 0,
        "value": "Maximum"
      }]
    },
    "other": {
      "value": [{
        "type": 0,
        "value": "("
      }, {
        "type": 1,
        "value": "unit"
      }, {
        "type": 0,
        "value": ") Maximum"
      }]
    }
  }
}], undefined, undefined, "en");
export const RangeSelect: FunctionComponent<Props> = memo(({
  className,
  id,
  min,
  max,
  values,
  unit,
  onChange
}) => {
  const [localValues, setLocalValues] = useState(values);
  const rootClassnames = classnames(styles.root, className);
  const inputId = capitalizeFirstLetter(id);
  useEffect(() => {
    setLocalValues(values);
  }, [values]);
  const onApply = useCallback((event: FocusEvent | KeyboardEvent, tapEnter?: boolean) => {
    let finalValues = {
      ...localValues
    };
    if (typeof finalValues.min !== 'number' || Number.isNaN(finalValues.min) || finalValues.min === null) {
      finalValues.min = tapEnter ? Math.ceil(min) : Math.ceil(values.min);
    }
    if (typeof finalValues.max !== 'number' || Number.isNaN(finalValues.max) || finalValues.max === null) {
      finalValues.max = tapEnter ? Math.floor(max) : Math.floor(values.max);
    }
    if (finalValues.min > finalValues.max) {
      finalValues = {
        min: Math.ceil(finalValues.max),
        max: Math.floor(finalValues.min)
      };
    }
    setLocalValues({
      min: Math.ceil(finalValues.min) || Math.ceil(values.min),
      max: Math.floor(finalValues.max) || Math.floor(values.max)
    });
    if (onChange && (Math.floor(values.max) !== Math.floor(finalValues.max) || Math.ceil(values.min) !== Math.ceil(finalValues.min))) {
      onChange(event, finalValues);
    }
  }, [localValues, max, min, onChange, values.max, values.min]);
  const handleInputMinChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setLocalValues({
      ...localValues,
      min: parseFloat(event.target.value)
    });
  }, [localValues]);
  const handleInputMaxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setLocalValues({
      ...localValues,
      max: parseFloat(event.target.value)
    });
  }, [localValues]);
  const selectAll = useCallback((e: FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);
  const onEnter = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') onApply(e, true);
  }, [onApply]);
  return <div className={rootClassnames}>
        <div className={styles.min}>
          <InputText type="number" id={`min${inputId}`} name="min" pattern="[0-9]*" label={translationsMin.format({
        unit
      }) as string} min={Math.floor(min)} max={Math.ceil(localValues.max) || max} value={Math.floor(localValues.min) || ''} onChange={handleInputMinChange} onBlur={onApply} onFocus={selectAll} onKeyDown={onEnter} />
        </div>
        <div className={styles.max}>
          <InputText type="number" id={`max${inputId}`} name="max" pattern="[0-9]*" label={translationsMax.format({
        unit
      }) as string} min={Math.floor(localValues.min) || 0} max={Math.ceil(max)} value={Math.ceil(localValues.max) || ''} onChange={handleInputMaxChange} onBlur={onApply} onFocus={selectAll} onKeyDown={onEnter} />
        </div>
      </div>;
});
RangeSelect.displayName = 'RangeSelect';